import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Inject, HostBinding, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import * as ModalPanelActions from 'render/render-shared/ui/render-modal-panel/store/render-modal-panel.actions';
import { getStringParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CookieService } from 'render/services/render-cookies.service';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { RenderState } from 'render/services/render-state/render-state.models';
import { BehaviorSubject, Observable, filter, of, switchMap, timer } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil, tap } from 'rxjs/operators';
import { InditexWindow } from 'shared/models/inditex-window.models';

import { PieceEventsSettings } from 'extensions/st/piece-events/front/models/piece-events.models';
import { Benefit, UserRegistrationStatus } from 'extensions/st/piece-events/front/services/api-events.models';
import { ApiEventsService } from 'extensions/st/piece-events/front/services/api-events.service';


@Component({
  selector: 'piece-events-render',
  templateUrl: './piece-events-render.component.html',
  styleUrls: ['piece-events-render.component.scss']

})
export class PieceEventsRenderComponent
  extends BaseRenderComponent<PieceEventsSettings> implements OnInit, OnChanges, OnDestroy {

  public eventData$?: Observable<Benefit[] | Benefit | undefined>;
  public accessToken?: string;
  public userRegistered?: UserRegistrationStatus;
  public storeId: string = '';
  public languageTag: string = '';
  public selectedEvent?: Benefit;
  public embedFrame: SafeHtml | string = '';

  public readonly array = Array;
  private readonly eventId$ = new BehaviorSubject<string>('');
  private embedFrameInitialized = false;
  private languageDisplayNames?: { of: (code: string) => string | undefined };
  private languageDisplayNamesLocale?: string;

  private readonly configs: Signal<GrowthbookConfig[]> = toSignal(
    this.renderGrowthbookService.configurations$,
    { initialValue: [] as GrowthbookConfig[] }
  );

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public get isLoggedIn(): boolean {
    return !!this.accessToken && !!this.window.inditex?.iUserJSON?.email;
  }

  public get reservationModalName(): string {
    return `event-reservation-modal-${this.piece?.id}`;
  }

  public get reservationConfirmationToastName(): string {
    return `event-reservation-confirmation-toast-${this.piece?.id}`;
  }

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly store: Store<RenderState>,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly apiEventsService: ApiEventsService,
    private readonly contextService: ContextService,
    private readonly cookieService: CookieService,
    private readonly renderEventsService: RenderEventsService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService
  ) {
    super();
    this.window.inditex?.updateUser();
  }

  @HostBinding('style.--background-color-event')
  public get backgroundColorEvent(): string {
    return getStringParam(this.configs(), 'content.the-collective.background-color', 'initial');
  }

  public ngOnInit(): void {
    this.accessToken = this.cookieService.getCookie('access_token');

    if (this.settings?.general?.showEmbedCode) {
      this.initializeEmbedFrameIfNoEvents(undefined);
      return;
    }

    this.initializeEventData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.component) {
      const { previousValue, currentValue } = changes.component;
      const previousGeneral = previousValue?.settings?.general;
      const currentGeneral = currentValue?.settings?.general;
      const shouldEnableEmbed = !previousGeneral?.showEmbedCode && currentGeneral?.showEmbedCode;
      const shouldRefreshEmbedCode = currentGeneral?.showEmbedCode
        && previousGeneral?.embedCode !== currentGeneral?.embedCode;

      if (shouldEnableEmbed || shouldRefreshEmbedCode) {
        this.eventData$ = undefined;
        this.embedFrameInitialized = false;
        this.initializeEmbedFrameIfNoEvents(undefined);
        return;
      }

      if (previousGeneral?.showEmbedCode && !currentGeneral?.showEmbedCode) {
        this.embedFrame = '';
        this.embedFrameInitialized = false;
        this.initializeEventData();
        return;
      }

      if (previousGeneral?.eventId !== currentGeneral?.eventId) {
        this.eventId$.next(currentGeneral?.eventId ?? '');
      }
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearBenefitId();
  }

  public replaceCapacityPlaceholder(text: string | null | undefined, capacity: number | undefined): string {
    if (!text || capacity === undefined) {
      return '';
    }

    return text
      .replace(/\bN\b/g, String(capacity))
      .replace(/\[|\]/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  public getLanguageDisplayName(languageCode?: string): string {
    if (!languageCode) {
      return '';
    }

    const normalizedLanguage = languageCode.toLowerCase().replace('_', '-').split('-')[0];
    const locale = this.lang || 'es';

    if (this.languageDisplayNamesLocale !== locale) {
      this.languageDisplayNames = this.createDisplayNames(locale);
      this.languageDisplayNamesLocale = locale;
    }

    const displayName = this.languageDisplayNames?.of(normalizedLanguage) || normalizedLanguage;
    return displayName.charAt(0).toLocaleUpperCase(locale) + displayName.slice(1);
  }

  public onReserveClick(event?: Benefit): void {
    if (this.isLoggedIn) {
      if (this.userRegistered) {
        return;
      }

      this.selectedEvent = event;
      this.openReservationModal();
      return;
    }

    const oauthLoginUrl = this.getOauthLoginUrl();
    if (oauthLoginUrl) {
      this.window.location.href = oauthLoginUrl;
      this.window.inditex?.updateUser();
    }
  }

  public onHowToGetThereClick(event: Event, data: Benefit): void {
    event.preventDefault();

    const renderEvent = {
      event,
      extra: {
        src: data.detailedInfo?.provider?.address?.url || '',
        identifier: 'how-to-get-there-link',
        type: 'external',
        title: this.piece?.settings?.general?.howToGetThereText || ''
      }
    } as RenderEvent;
    this.renderEventsService.events.next(renderEvent);
  }

  public onCancelReservation(eventId: string): void {
    this.apiEventsService.cancelReservation(
      this.languageTag,
      this.storeId,
      eventId,
      this.accessToken
    ).pipe(take(1))
      .subscribe(() => {
        this.userRegistered = undefined;
        this.ngOnInit();
      });
  }

  public onReservationRegistered(status?: UserRegistrationStatus): void {
    this.userRegistered = status;

    if (status === 'REGISTERED') {
      this.store.dispatch(ModalPanelActions.openModal({
        modalName: this.reservationConfirmationToastName
      }));
      timer(3000).pipe(
        takeUntil(this.clearSubscriptions$)
      ).subscribe(() => {
        this.store.dispatch(ModalPanelActions.closeModal({
          modalName: this.reservationConfirmationToastName
        }));
      });
    }
  }

  public openReservationModal(): void {
    this.store.dispatch(ModalPanelActions.openModal({
      modalName: this.reservationModalName
    }));
  }

  public getOauthLoginUrl(): string {
    const langCode = this.lang?.toLowerCase() ?? '';
    const storeCode = this.country?.toUpperCase() ?? '';
    const url = this.window.inditex?.oauth?.getOauthLoginUrl({
      langCode,
      language: this.languageTag,
      storeCode,
      storeId: this.storeId,
      redirectUri: this.window.location.href,
      hlpzi: this.getHlpziEncoded(),
      channel: this.isMobile ? 'web-mobile' : 'web',
      guestFlow: true
    }) ?? '';

    return `${this.window.location.origin}${url}`;
  }

  public isComingSoon(event?: Benefit): boolean {
    const detailedInfo = event?.detailedInfo;
    if (!detailedInfo?.isComingSoon) {
      return false;
    }

    const comingSoonEndDate = detailedInfo.comingSoonEndDateTime
      ? new Date(detailedInfo.comingSoonEndDateTime)
      : undefined;

    if (!comingSoonEndDate || Number.isNaN(comingSoonEndDate.getTime())) {
      return false;
    }

    return new Date() < comingSoonEndDate;
  }

  public isRegistrationClosed(event?: Benefit): boolean {
    const detailedInfo = event?.detailedInfo;
    if (!detailedInfo) {
      return false;
    }

    if (!detailedInfo.registrationCutOffDateTime) {
      return false;
    }

    const cutOffDate = new Date(detailedInfo.registrationCutOffDateTime);
    if (Number.isNaN(cutOffDate.getTime())) {
      return false;
    }

    return new Date() >= cutOffDate;
  }

  private initializeEventData(): void {
    this.eventId$.next(this.settings?.general?.eventId ?? '');
    this.eventData$ = this.contextService.getContext$().pipe(
      map((context: EcommerceContext) => ({
        storeId: context?.store?.id,
        storeAvailability: context?.store?.details?.storeAvailability,
        lang: this.normalizeLanguageTag(
          context?.store?.supportedLanguages?.find((l) => l.code === this.lang)?.languageTag
        )
      })),
      filter(({ storeId, storeAvailability, lang }) => !!storeId && !!storeAvailability && !!lang),
      switchMap((context) => this.eventId$.pipe(
        distinctUntilChanged(),
        switchMap((eventId) => {
          this.storeId = context.storeId;
          this.languageTag = context.lang!;
          return this.apiEventsService.search(
            this.languageTag,
            this.storeId,
            eventId || undefined
          ).pipe(
            map(({ benefit, benefits }) => benefit ?? benefits),
            switchMap((eventData) => this.updateReservationStatus(eventData, context.storeId)),
            tap((eventData) => {
              this.initializeEmbedFrameIfNoEvents(eventData);
              this.saveBenefitId(eventData);
            })
          );
        })
      )),
      takeUntil(this.clearSubscriptions$)
    );
  }

  private getHlpziEncoded(): string {
    const payload = this.getZenitAnalyticsModel();
    const jsonPayload = JSON.stringify(payload);

    return this.window.btoa(jsonPayload);
  }

  private initializeEmbedFrameIfNoEvents(eventData: Benefit[] | Benefit | undefined): void {
    if (this.embedFrameInitialized || this.hasEventData(eventData)) {
      return;
    }

    this.embedFrameInitialized = true;
    this.embedFrame = this.apiEventsService.embed(this.settings?.general?.embedCode ?? '');
  }

  private hasEventData(eventData: Benefit[] | Benefit | undefined): boolean {
    return Array.isArray(eventData) ? eventData.length > 0 : !!eventData;
  }

  private updateReservationStatus(
    eventData: Benefit[] | Benefit | undefined,
    storeId: string
  ): Observable<Benefit[] | Benefit | undefined> {
    const benefitId = this.getRenderedBenefitId(eventData);

    if (!this.accessToken || !benefitId || !this.country || !this.lang) {
      this.userRegistered = undefined;
      return of(eventData);
    }

    return this.apiEventsService.getReservation(
      this.languageTag,
      storeId,
      this.accessToken
    ).pipe(
      map(({ benefit, benefits }) => {
        const reservedBenefits = benefit ? [benefit] : (benefits ?? []);
        const match = reservedBenefits.find((reservedBenefit) => reservedBenefit.id === benefitId);
        this.userRegistered = match?.detailedInfo?.userRegistrationStatus;
        return eventData;
      })
    );
  }

  private getRenderedBenefitId(eventData: Benefit[] | Benefit | undefined): string | undefined {
    const currentEvent = Array.isArray(eventData) ? eventData[0] : eventData;

    return currentEvent?.id;
  }

  private normalizeLanguageTag(languageTag?: string): string {
    return languageTag?.replace(/_/g, '-') ?? '';
  }

  private createDisplayNames(locale: string): { of: (code: string) => string | undefined } | undefined {
    const DisplayNamesConstructor = (Intl as { DisplayNames?: new (locales: string[], options: { type: 'language' }) => { of: (code: string) => string | undefined } }).DisplayNames;
    if (!DisplayNamesConstructor) {
      return undefined;
    }

    try {
      return new DisplayNamesConstructor([locale], { type: 'language' });
    } catch {
      return undefined;
    }
  }

  private getZenitAnalyticsModel() {
    const gaCookie = this.cookieService.getCookie('_ga') || '';
    const { os, osVersion } = this.getOsData();
    const colorScheme = this.window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
    const { width, height } = this.window.screen;
    const displayResolution = `${width}x${height}`;
    const environment = this.getEnvironment(this.window.location.hostname);

    return {
      device: {
        type: this.isMobile ? 'mobile' : 'desktop',
        os,
        osVersion,
        language: this.lang,
        clientId: this.getClientId(gaCookie),
        displayResolution,
        colorScheme
      },
      endpoints: {
        basePath: '/zenit/topics',
        domain: this.getEventTrackerDomain(environment),
        environment,
        version: 'v1'
      },
      extra: { gaCookie },
      geo: {
        region: '',
        regionGroupId: 0,
        country: this.country?.toUpperCase()
      },
      privacyConsent: this.getPrivacyConsentParams(),
      store: { id: Number(this.storeId) },
      tenant: 'st',
      userAgent: {
        appName: 'com.inditex.stradivarius',
        appVersion: this.window.inditex?.iFrontVersion,
        platform: 'web',
        uaString: this.window.navigator?.userAgent,
        isEmbedded: this.isWebView(),
        ...(this.getCookiesAllowed() && { UAITXID: this.cookieService.getCookie('UAITXID') }),
        ...(this.getRecommendationAllowed() && { ITXPERSONALIZE: this.cookieService.getCookie('ITXDEVICEID') })
      },
      user: {
        language: `${(this.lang)}_${(this.country)}`,
        identity: this.getIdentityParams(),
        session: { id: this.cookieService.getCookie('ITXSESSIONID') }
      }
    };
  }

  private getClientId(gaCookie: string): string {
    return this.getCookiesAllowed() ? gaCookie?.split('.').slice(2).join('.') : '';
  }

  private getEnvironment(host: string): string {
    if (host.startsWith('pre')) {
      return 'pre';
    }

    if (host.startsWith('des')) {
      return 'des';
    }

    return 'pro';
  }

  private getEventTrackerDomain(environment: string): string {
    return environment === 'pro'
      ? 'https://event-tracker.inditex.com'
      : 'https://event-tracker-pre.inditex.com';
  }

  private getOsData(): { os: string; osVersion: string } {
    const userAgent = this.window.navigator?.userAgent ?? '';

    const androidMatch = userAgent.match(/Android\s([\d.]+)/i);
    if (androidMatch) {
      return { os: 'android', osVersion: androidMatch[1] };
    }

    const iosMatch = userAgent.match(/(?:iPhone|iPad|iPod).*OS\s([\d_]+)/i);
    if (iosMatch) {
      return { os: 'ios', osVersion: iosMatch[1].replace(/_/g, '.') };
    }

    const macosMatch = userAgent.match(/Mac OS X\s([\d_.]+)/i);
    if (macosMatch) {
      return { os: 'macos', osVersion: macosMatch[1].replace(/_/g, '.') };
    }

    const windowsMatch = userAgent.match(/Windows NT\s([\d.]+)/i);
    if (windowsMatch) {
      return { os: 'windows', osVersion: windowsMatch[1] };
    }

    if (/Linux/i.test(userAgent)) {
      return { os: 'linux', osVersion: '' };
    }

    return { os: '', osVersion: '' };
  }

  private getCookiesAllowed(): boolean {
    return this.window.inditex?.cookieConsent?.isGroupAllowed(this.window.inditex?.iCookieGroupNames?.analytics)
      ?? false;
  }

  private getRecommendationAllowed(): boolean {
    return this.window.inditex?.cookieConsent?.isGroupAllowed(this.window.inditex?.iCookieGroupNames?.personalization)
      ?? false;
  }

  private getIdentityParams() {
    if (this.window.inditex?.iUserJSON && this.getCookiesAllowed()) {
      const usrModel = this.window.inditex.iUserJSON;
      return {
        ...(this.accessToken && {
          userId: usrModel.identity.userId,
          token: usrModel.userToken
        }),
        kind: usrModel.userType === 'R' ? 'registered' : 'guest'
      };
    }
    return {};
  }

  private getPrivacyConsentParams(): { consentCategories: string; consentedDate?: string } {
    let consentCategoriesValue = '';
    const groups = this.window.inditex?.cookieConsent?.getGroups();

    if (groups) {
      groups.forEach((cc, i) => {
        consentCategoriesValue
        += `${cc.id
          }:${
            cc.allowed ? '1' : '0'
          }${i < groups.length - 1 ? ',' : ''}`;
      });
    }

    return {
      consentCategories: consentCategoriesValue,
      consentedDate:
      this.cookieService.getCookie('OptanonAlertBoxClosed') || (this.getCookiesAllowed() ? new Date().toISOString() : undefined)
    };
  }

  private isWebView(): boolean {
    const userAgent = this.window.navigator?.userAgent ?? '';

    if (/\bwv\b|webview/i.test(userAgent)) {
      return true;
    }

    if (/iPhone|iPad|iPod/.test(userAgent) && !/Safari/.test(userAgent)) {
      return true;
    }

    return false;
  }

  private saveBenefitId(eventData: Benefit[] | Benefit | undefined): void {
    if (!eventData) {
      return;
    }
    this.clearBenefitId();
    const benefitId = Array.isArray(eventData) ? eventData[0]?.id : eventData?.id;

    if (benefitId) {
      sessionStorage.setItem('benefit_id', benefitId);
    }
  }

  private clearBenefitId(): void {
    sessionStorage.removeItem('benefit_id');
  }

}
